import React, { useEffect, useRef, useState } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  NavLink,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { GAMES, SHARED_RULES, gameBySlug, type Game } from "./games";
import { SERVICES, ENTRY_POINTS, STUDIO } from "./services";
import { usePageMeta } from "./usePageMeta";
import EnquiryForm from "./EnquiryForm";

// ---------------------------------------------
// Main App Component
// ---------------------------------------------
function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/games" element={<GamesPage />} />
        <Route path="/games/:slug" element={<GamePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/how-to-play" element={<HowToPlayPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* The site advertised one game under a name that no longer exists.
            Anything already linked or bookmarked lands on its successor. */}
        <Route path="/cryptid-hunters" element={<Navigate to="/games/cryptid-files" replace />} />
        <Route path="/cryptid-files" element={<Navigate to="/games/cryptid-files" replace />} />
        <Route path="/fish-tales" element={<Navigate to="/games/fish-tales" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

/**
 * A hash router leaves you scrolled where the last page left off, which on a
 * phone means tapping a nav item and landing in the middle of a paragraph.
 *
 * The router owns the URL's only hash, so an in-page anchor arrives as a
 * *second* one (`#/how-to-play#fish-tales`) that the browser will not act
 * on by itself. Honour it here, and fall back to the top of the page.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

// ---------------------------------------------
// Responsive helpers
// ---------------------------------------------

// The full nav bar stops fitting alongside the logo below this width.
const COMPACT_NAV_QUERY = "(max-width: 1199px)";

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

// Shared fluid spacing so every section lines up at any width.
const GUTTER = "clamp(16px, 4vw, 60px)";
const SECTION_PADDING_Y = "clamp(32px, 6vw, 60px)";

const NAV_ITEMS = [
  { to: "/", label: "HOME" },
  { to: "/games", label: "GAMES" },
  { to: "/services", label: "SERVICES" },
  { to: "/how-to-play", label: "HOW TO PLAY" },
  { to: "/media", label: "MEDIA" },
  { to: "/contact", label: "CONTACT" },
];

/**
 * React 18 has no `fetchPriority` prop — it warns and drops the attribute. The
 * hero image is the largest paint on every page and genuinely wants the hint,
 * so spell it the way HTML does and spread it on.
 */
const HIGH_PRIORITY = { fetchpriority: "high" } as unknown as
  React.ImgHTMLAttributes<HTMLImageElement>;

/**
 * Where the studio actually is.
 *
 * The design draws four marks — Discord, Instagram, YouTube and X. Two of
 * those are accounts that do not exist, and a footer icon pointing at nothing
 * is worse than a gap, so this is the real set: the same three the Contact
 * page lists, and no others until there are others.
 */
const SOCIAL_LINKS = [
  {
    label: "Neon Crow Games on Discord",
    href: "https://discord.gg/Hs8eGcfp32",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.27 5.33A16.4 16.4 0 0 0 15.2 4l-.24.47a12.6 12.6 0 0 1 3.6 1.67 14.6 14.6 0 0 0-12.9 0A12.6 12.6 0 0 1 9.3 4.47L9.06 4A16.4 16.4 0 0 0 5 5.33C2.44 9.1 1.75 12.78 2.1 16.4a16.5 16.5 0 0 0 5 2.54l1.09-1.5a10.7 10.7 0 0 1-1.7-.82l.42-.32a11.8 11.8 0 0 0 10.2 0l.42.32c-.54.32-1.11.6-1.7.82l1.08 1.5a16.5 16.5 0 0 0 5-2.54c.42-4.2-.67-7.85-2.64-11.07ZM9.35 14.4c-.98 0-1.79-.9-1.79-2s.79-2 1.79-2 1.8.9 1.79 2c0 1.1-.79 2-1.79 2Zm5.3 0c-.98 0-1.79-.9-1.79-2s.79-2 1.79-2 1.8.9 1.79 2c0 1.1-.79 2-1.79 2Z" />
      </svg>
    ),
  },
  {
    label: "Neon Crow Games on X",
    href: "https://x.com/neon_crow_games",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.53 3h3.14l-6.86 7.84L22 21h-6.32l-4.95-6.47L5.07 21H1.93l7.34-8.39L2 3h6.48l4.47 5.91L17.53 3Zm-1.1 16.13h1.74L7.65 4.78H5.79l10.64 14.35Z" />
      </svg>
    ),
  },
  {
    label: "Neon Crow Games on Facebook",
    href: "https://www.facebook.com/profile.php?id=61562906850303",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z" />
      </svg>
    ),
  },
];

const APP_URL = "https://app.neoncrowgames.com";
const LOGIN_URL = `${APP_URL}/login`;
const REGISTER_URL = `${APP_URL}/register`;
const SITE_URL = "https://neoncrowgames.com";
// A subject line saves the studio guessing which half of the site someone came
// from, and saves the sender writing one.
const CONTACT_MAILTO =
  "mailto:neoncrowgames@gmail.com?subject=" + encodeURIComponent("Studio services enquiry");

// ---------------------------------------------
// Head
// ---------------------------------------------

/**
 * Per-page title, description and social card. Renders nothing — it only drives
 * the hook that writes into <head>. See `usePageMeta` for what this can and
 * cannot do on a client-rendered site.
 */
function PageMeta({
  title,
  description,
  image = "/social-card.jpg",
}: {
  title: string;
  description: string;
  image?: string;
}) {
  const { pathname } = useLocation();
  const fullTitle = title === "Neon Crow Games" ? title : `${title} — Neon Crow Games`;

  usePageMeta({
    title: fullTitle,
    description,
    image: `${SITE_URL}${image}`,
    // HashRouter keeps the route in the fragment, so that is what a canonical
    // URL for this page has to carry.
    url: `${SITE_URL}/#${pathname}`,
  });

  return null;
}

// ---------------------------------------------
// Layout Components
// ---------------------------------------------
function SiteLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="site-shell">
      <SiteHeader />
      <main>{children}</main>
      <SiteFooter />
    </div>
  );
}

function PageShell({
  children,
  maxWidth = "1200px",
}: {
  children: React.ReactNode;
  maxWidth?: string;
}) {
  return (
    <div
      style={{
        maxWidth,
        margin: "0 auto",
        padding: `${SECTION_PADDING_Y} ${GUTTER}`,
      }}
    >
      {children}
    </div>
  );
}

function SiteHeader() {
  const isCompact = useMediaQuery(COMPACT_NAV_QUERY);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeMenu = () => setMenuOpen(false);

  // The drawer only exists in the compact layout — drop it if the viewport grows.
  useEffect(() => {
    if (!isCompact) setMenuOpen(false);
  }, [isCompact]);

  // Browser back out of a page closes the drawer with it; otherwise it hangs
  // open over whatever you navigated to.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      // Escape should leave focus somewhere sensible, not on a hidden link.
      toggleRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);

    // Stop the page behind the drawer from scrolling under your thumb. iOS
    // ignores `overflow: hidden` on <body>, so the position is pinned too and
    // restored — with the scroll offset — on close.
    const scrollY = window.scrollY;
    const { body } = document;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [menuOpen]);

  return (
    <header className="site-header">
      <div className="site-header__bar" style={{ padding: `clamp(10px, 2vw, 20px) ${GUTTER}` }}>
        <NavLink to="/" onClick={closeMenu} aria-label="Neon Crow Games home" className="brand">
          <img
            className="brand__mark"
            src="/neon-crow-bird.webp"
            alt=""
            width={552}
            height={469}
          />
          {/* The wordmark is set rather than drawn: it stays crisp at every
              size, it is selectable, and a screen reader gets the studio's
              name instead of alt text pretending to be one. */}
          <span className="brand__words">
            <span className="brand__name">NEON CROW</span>
            <span className="brand__sub">GAMES</span>
          </span>
        </NavLink>

        {isCompact ? (
          <button
            type="button"
            ref={toggleRef}
            className="hamburger"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="hamburger__bar" />
            <span className="hamburger__bar" />
            <span className="hamburger__bar" />
          </button>
        ) : (
          <nav style={{ display: "flex", gap: "clamp(14px, 2vw, 36px)", alignItems: "center" }}>
            {/* Home is the logo's job in this layout, as it is in the design.
                It stays in the drawer below, where there is no logo to tap. */}
            {NAV_ITEMS.filter((item) => item.to !== "/").map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `nav-link nav-link--new${isActive ? " nav-link--active" : ""}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a className="btn btn--outline btn--sm" href={LOGIN_URL}>
              LOG IN
            </a>
            <p className="header-aside" aria-hidden="true">{"Curious\nPeople\nPlay Here."}</p>
          </nav>
        )}
      </div>

      {isCompact && menuOpen && (
        <nav id="site-menu" className="mobile-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `nav-link${isActive ? " nav-link--active" : ""}`}
              onClick={closeMenu}
            >
              {item.label}
            </NavLink>
          ))}
          <div style={{ display: "grid", gap: "10px", padding: `12px ${GUTTER} 20px` }}>
            <a className="neon-btn neon-btn--sm" href={REGISTER_URL} onClick={closeMenu}>
              PLAY FREE
            </a>
            <a className="neon-btn neon-btn--sm neon-btn--ghost" href={LOGIN_URL} onClick={closeMenu}>
              LOG IN
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}

function SiteFooter() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await fetch("https://api.neoncrowgames.com/api/subscribers/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      setMessage(data.message);
      // Trust the flag the API sends rather than sniffing its prose for the
      // word "error" — a perfectly good refusal ("already subscribed") was
      // being coloured as success.
      setIsError(!data.success);

      if (data.success) {
        setEmail("");
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      setMessage("An error occurred. Please try again later.");
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Newsletter Footer */}
      <footer className="newsletter" style={{ padding: "clamp(28px, 5vw, 40px) 0" }}>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "clamp(20px, 4vw, 32px)",
          maxWidth: "1400px",
          margin: "0 auto",
          padding: `0 ${GUTTER}`
        }}>
          <div style={{ flex: "1 1 300px", minWidth: 0 }}>
            <p className="newsletter__title">
              Something strange arrives in the post now and then. Nothing else.
            </p>
            {message && (
              <div
                role="status"
                style={{
                  marginTop: "10px",
                  fontSize: "16px",
                  color: isError ? "#ff6b6b" : "#00ffff",
                  textShadow: isError
                    ? "0 0 10px rgba(255, 107, 107, 0.5)"
                    : "0 0 10px rgba(0, 255, 255, 0.5)"
                }}
              >
                {message}
              </div>
            )}
          </div>
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              flex: "1 1 320px",
              minWidth: 0,
              maxWidth: "620px"
            }}
          >
            <input
              className="neon-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              aria-label="Email address"
              autoComplete="email"
              inputMode="email"
              required
              style={{ flex: "1 1 200px", minWidth: 0 }}
            />
            <button
              className="btn btn--ember"
              type="submit"
              disabled={isSubmitting}
              style={{ flex: "0 1 auto" }}
            >
              {isSubmitting ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </footer>

      {/*
        The design's footer: the mark and the studio's name, the sections, the
        places you can find us, and a sign-off. The newsletter above it is not
        in the drawing and is kept anyway — it is the only way the site hears
        back from anyone.
      */}
      <footer className="site-foot">
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: `0 ${GUTTER}` }}>
          <div className="site-foot__inner">
            <a href="#/" className="brand" aria-label="Neon Crow Games home">
              <img
                className="brand__mark"
                src="/neon-crow-bird.webp"
                alt=""
                width={552}
                height={469}
                loading="lazy"
                style={{ width: "34px" }}
              />
              <span className="brand__words">
                <span className="brand__name" style={{ fontSize: "15px" }}>NEON CROW</span>
                <span className="brand__sub" style={{ fontSize: "9px" }}>GAMES</span>
              </span>
            </a>

            <nav className="site-foot__nav">
              {NAV_ITEMS.filter((item) => item.to !== "/").map((item) => (
                <a key={item.to} href={`#${item.to}`} className="nav-link nav-link--new">
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="site-foot__social">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div
            className="site-foot__inner"
            style={{ marginTop: "clamp(16px, 2vw, 26px)", alignItems: "flex-end" }}
          >
            <p className="site-foot__legal" style={{ margin: 0 }}>
              &copy; {new Date().getFullYear()} Neon Crow Games. All rights reserved.{" "}
              <a href="#/privacy" className="text-link text-link--muted">Privacy</a>
              {" · "}
              <a href="mailto:neoncrowgames@gmail.com" className="text-link text-link--muted">
                neoncrowgames@gmail.com
              </a>
            </p>
            <p className="site-foot__sign" style={{ margin: "0 0 0 auto" }}>
              Play
              <br />
              more
              <br />
              curiously.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

// ---------------------------------------------
// Shared pieces
// ---------------------------------------------

function StatusPill({ game }: { game: Game }) {
  const live = game.status === "live";
  return (
    <span className={`pill ${live ? "pill--live" : "pill--soon"}`}>
      {live ? "Playable now" : "Coming soon"}
    </span>
  );
}

/** The two buttons every game gets, in the order they should be tapped. */
function PlayActions({ game, block = false }: { game: Game; block?: boolean }) {
  const style = block ? { width: "100%" } : undefined;
  return (
    <div className="actions">
      {game.status === "live" ? (
        <>
          <a className="neon-btn" href={REGISTER_URL} style={style}>
            PLAY FREE
          </a>
          <a className="neon-btn neon-btn--ghost" href={`#/how-to-play#${game.slug}`} style={style}>
            HOW TO PLAY
          </a>
        </>
      ) : (
        <a className="neon-btn" href={`#/games/${game.slug}`} style={style}>
          LEARN MORE
        </a>
      )}
    </div>
  );
}

/** The arrow that follows a call to action. Decorative — the label says it. */
function Arrow() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The three facts a card carries. Drawn rather than pulled from an icon font:
 * three glyphs is not worth a network request, and these inherit the colour
 * of the line they sit on.
 */
const FACT_ICONS = {
  players: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M16 19v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V19M9 9.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM22 19v-1.5a4 4 0 0 0-3-3.87M16 3.63a4 4 0 0 1 0 7.75"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  length: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7v5.2l3.2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
        strokeLinejoin="round" />
    </svg>
  ),
  tag: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 12.4V4a1 1 0 0 1 1-1h8.4a1 1 0 0 1 .7.3l7.6 7.6a1 1 0 0 1 0 1.4l-8.4 8.4a1 1 0 0 1-1.4 0L3.3 13.1a1 1 0 0 1-.3-.7Z"
        stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="7.8" cy="7.8" r="1.4" fill="currentColor" />
    </svg>
  ),
};

/**
 * A game on the front page: the scene, then the name over the foot of it, then
 * the three facts someone decides on — who it seats, how long it takes, and
 * what kind of evening it is.
 *
 * The cover art, not the card fan. At this size a five-card fan is five
 * illegible slivers, and the front page is selling the evening rather than the
 * components.
 */
function GameCard({ game, kickerRight = false }: { game: Game; kickerRight?: boolean }) {
  const href = `#/games/${game.slug}`;
  // "30–45 minutes" is a sentence; the meta row is a label.
  const length = game.length.replace(" minutes", " min").replace(" minute", " min");

  return (
    <article className="game-card">
      <a className="game-card__art" href={href} aria-label={`${game.name} — read more`}>
        <img src={game.cover} alt="" width={887} height={559} loading="lazy" decoding="async" />
      </a>
      <p
        className={`game-card__kicker${kickerRight ? " game-card__kicker--right" : ""}`}
        aria-hidden="true"
      >
        {game.kicker.map((word) => (
          <span key={word}>{word}</span>
        ))}
      </p>
      <div className="game-card__body">
        <h3 className="game-card__title u-display">
          <a href={href}>{game.name}</a>
        </h3>
        <p className="game-card__blurb u-serif">{game.tagline}</p>
        <div className="game-card__meta">
          <span className="game-card__fact">{FACT_ICONS.players}{game.players}</span>
          <span className="game-card__fact">{FACT_ICONS.length}{length}</span>
          <span className="game-card__fact">{FACT_ICONS.tag}{game.tag}</span>
          {game.status === "soon" && <span className="game-card__soon">Coming soon</span>}
          <a className="game-card__cta" href={href}>
            View game <Arrow />
          </a>
        </div>
      </div>
    </article>
  );
}

/**
 * A game in the grid. The cover, not the fan — at grid size a five-card fan is
 * five illegible slivers.
 */
function GameTile({ game }: { game: Game }) {
  return (
    <article className="tile">
      <a href={`#/games/${game.slug}`} className="tile__art" aria-label={`${game.name} — read more`}>
        <img
          src={game.cover}
          alt=""
          width={887}
          height={559}
          loading="lazy"
          decoding="async"
        />
      </a>
      <div className="tile__body">
        <div className="tile__meta">
          <StatusPill game={game} />
          <span className="tile__players">{game.players}</span>
        </div>
        <h3 className="tile__title" style={{ color: game.accent }}>
          <a href={`#/games/${game.slug}`} className="tile__link">{game.name}</a>
        </h3>
        <p className="tile__tagline">{game.tagline}</p>
        <PlayActions game={game} block />
      </div>
    </article>
  );
}

/**
 * The card gallery. A scroll-snapping row on a phone — a grid that shrinks six
 * cards to thumbnail size would make the art unreadable, which is the one thing
 * this section exists to avoid.
 */
function CardStrip({ game }: { game: Game }) {
  return (
    <div className="card-strip" role="list" aria-label={`Cards from ${game.name}`}>
      {game.cards.map((card) => (
        <figure className="card-strip__item" role="listitem" key={card.src}>
          <img
            src={card.src}
            alt={card.alt}
            width={440}
            height={616}
            loading="lazy"
            decoding="async"
          />
        </figure>
      ))}
    </div>
  );
}

// ---------------------------------------------
// Page Components
// ---------------------------------------------
function HomePage() {
  return (
    <SiteLayout>
      <PageMeta
        title="Neon Crow Games"
        description="Tabletop games about cryptids, mysteries and unforgettable stories. Five of them, free in your browser: Cryptid Files, Fish Tales, Triad Paranormal, Architect: War Rigs and Heroes 3: Bounties."
      />

      {/*
        The hero is a scene, not a product shot. It is the one place on the
        site that says what kind of evening this is before it says what is in
        the box — everything below it is specifics.
      */}
      <section className="hero-band">
        <img
          className="hero-band__art"
          src="/hero.webp"
          alt=""
          width={2400}
          height={980}
          // The largest paint on the page: never lazy, and asked for early so
          // it is not queued behind the logo.
          {...HIGH_PRIORITY}
          decoding="async"
        />
        <div className="hero-band__scrim" />
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: `0 ${GUTTER}`,
            width: "100%",
          }}
        >
          <div className="hero-band__copy">
            <p className="u-label">Tabletop games for bolder imaginations</p>
            <h1 className="hero-title u-display">
              <span className="hero-title__line">Games for the</span>
              <span className="hero-title__accent">Strange &amp; Curious</span>
            </h1>
            <p className="hero-lede u-serif">
              We make tabletop games about cryptids, mysteries and unforgettable
              stories — for curious people everywhere.
            </p>
            <div className="hero-actions">
              <a className="btn btn--ember" href="#/games">
                Explore our games <Arrow />
              </a>
              <a className="btn btn--outline" href={REGISTER_URL}>
                Play free
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="games">
        <PageShell maxWidth="1400px">
          <div className="rule-head">
            <div className="rule-head__line">
              <h2 className="rule-head__title u-display">Our Games</h2>
            </div>
            <p className="u-label">Different worlds. A shared sense of wonder.</p>
          </div>
          <div className="game-grid">
            {GAMES.map((game, i) => (
              <GameCard key={game.slug} game={game} kickerRight={i % 2 === 1} />
            ))}
          </div>
        </PageShell>
      </section>

      <section className="philosophy">
        <PageShell maxWidth="1400px">
          <div className="philosophy__inner">
            <div className="philosophy__crow">
              <img src="/crow.webp" alt="" width={552} height={469} loading="lazy" />
            </div>
            <div>
              <p className="u-label u-label--cyan">{STUDIO.eyebrow}</p>
              <h2 className="philosophy__title u-display">{STUDIO.title}</h2>
              <p className="philosophy__body u-serif">{STUDIO.body}</p>
              <div style={{ marginTop: "clamp(18px, 2vw, 26px)" }}>
                <a className="btn btn--outline btn--sm" href="#/services">
                  We build other people's games too
                </a>
              </div>
            </div>
            <p
              className="philosophy__script u-script"
              aria-hidden="true"
              style={{ whiteSpace: "pre-line" }}
            >
              {STUDIO.script}
            </p>
          </div>
        </PageShell>
      </section>
    </SiteLayout>
  );
}

function GamesPage() {
  return (
    <SiteLayout>
      <PageMeta
        title="Games"
        description="Five free browser card games from Neon Crow Games: Cryptid Files, Fish Tales, Triad Paranormal, Architect: War Rigs and Heroes 3: Bounties."
      />
      <PageShell maxWidth="1400px">
        <h1 style={pageHeadingStyle}>GAMES</h1>
        <p style={pageDescriptionStyle}>
          Five games so far. All free, all in the browser, two to four players depending
          on the game.
        </p>
        <div className="tile-grid" style={{ marginTop: "clamp(28px, 5vw, 48px)" }}>
          {GAMES.map((game) => (
            <GameTile key={game.slug} game={game} />
          ))}
        </div>
      </PageShell>
    </SiteLayout>
  );
}

function GamePage() {
  const { slug } = useParams();
  const game = slug ? gameBySlug(slug) : undefined;

  if (!game) return <Navigate to="/games" replace />;

  return (
    <SiteLayout>
      {/* No per-game og:image. The covers are WebP, which several link
          scrapers still refuse, and none of them execute the JavaScript that
          would set it anyway — see `usePageMeta`. One JPEG for the whole site
          is the image that actually gets used. */}
      <PageMeta title={game.name} description={game.tagline} />
      <PageShell maxWidth="1200px">
        <div className="hero hero--game">
          <div className="hero__copy">
            <StatusPill game={game} />
            {/* Sized off the LONGEST WORD, not the whole name. The display
                face is wide and this column is half of a 1200px shell, so a word
                over eight characters does not fit at the full display size —
                and `.hero__title`'s overflow-wrap then hyphenates it mid-word
                ("PARANOR / MAL"). Titles that already fit are left alone. */}
            <h1
              className="hero__title hero__title--game"
              style={{
                color: game.accent,
                ...(Math.max(...game.name.split(/\s+/).map((w) => w.length)) > 8
                  ? { fontSize: "clamp(32px, 5.6vw, 56px)" }
                  : {}),
              }}
            >
              {game.name.toUpperCase()}
            </h1>
            <p className="hero__lede">{game.blurb}</p>
            <ul className="facts">
              <li>{game.players}</li>
              <li>{game.length}</li>
              <li>Free in your browser</li>
            </ul>
            {game.statusNote && <p className="note">{game.statusNote}</p>}
            <PlayActions game={game} />
          </div>
          <div className="hero__art">
            <img
              className="hero-art"
              src={game.fan}
              alt={`A hand of ${game.name} cards`}
              width={1521}
              height={742}
              {...HIGH_PRIORITY}
              decoding="async"
            />
          </div>
        </div>
      </PageShell>

      <section className="band">
        <PageShell maxWidth="1200px">
          <h2 className="section-title">WHAT YOU PLAY WITH</h2>
          <div className="component-grid">
            {game.components.map((component) => (
              <div className="component" key={component.label}>
                <div className="component__count" style={{ color: game.accent }}>
                  {component.count}
                </div>
                <div className="component__label">{component.label}</div>
                <p className="component__detail">{component.detail}</p>
              </div>
            ))}
          </div>
        </PageShell>
      </section>

      <PageShell maxWidth="1200px">
        <h2 className="section-title">A TURN</h2>
        <TurnList game={game} />

        <h2 className="section-title" style={{ marginTop: "clamp(36px, 6vw, 60px)" }}>
          HOW IT ENDS
        </h2>
        {game.victory.map((line) => (
          <p className="prose" key={line}>{line}</p>
        ))}
      </PageShell>

      <section className="band">
        <PageShell maxWidth="1400px">
          <h2 className="section-title">THE CARDS</h2>
          <CardStrip game={game} />
        </PageShell>
      </section>

      <PageShell maxWidth="1000px">
        <div className="cta">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            {game.status === "live" ? "PLAY IT NOW" : "BE THERE FOR LAUNCH"}
          </h2>
          <p className="prose" style={{ textAlign: "center" }}>
            {game.status === "live"
              ? "Free, in the browser, no download. Bring one to three friends."
              : "Leave an email at the bottom of the page and you will hear the day it opens."}
          </p>
          {game.status === "live" && (
            <div className="actions actions--center">
              <a className="neon-btn" href={REGISTER_URL}>PLAY FREE</a>
              <a className="neon-btn neon-btn--ghost" href={LOGIN_URL}>LOG IN</a>
            </div>
          )}
        </div>
      </PageShell>
    </SiteLayout>
  );
}

function TurnList({ game }: { game: Game }) {
  return (
    <ol className="turn-list">
      {game.turn.map((step, i) => (
        <li className="turn-step" key={step.step}>
          <span className="turn-step__number" style={{ borderColor: game.accent, color: game.accent }}>
            {i + 1}
          </span>
          <div>
            <div className="turn-step__name">{step.step}</div>
            <p className="turn-step__detail">{step.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ServicesPage() {
  return (
    <SiteLayout>
      <PageMeta
        title="Studio Services"
        description="Digital adaptation of physical card and board games, plus development, rules audits, rulebook editing, playtesting and original design."
      />
      <PageShell maxWidth="1200px">
        <h1 style={pageHeadingStyle}>STUDIO SERVICES</h1>
        <p style={pageDescriptionStyle}>
          We build our own games, and we build other people's. Whether yours is a sketch
          or a printed box, the work starts the same way — a conversation about what it
          actually needs.
        </p>

        <div className="doors">
          {ENTRY_POINTS.map((door) => (
            <div className="door" key={door.title}>
              <h2 className="door__title">{door.title}</h2>
              <p className="door__body">{door.body}</p>
              <a className="neon-btn neon-btn--sm" href="#/services#enquire">
                {door.cta}
              </a>
            </div>
          ))}
        </div>
      </PageShell>

      <section className="band">
        <PageShell maxWidth="1200px">
          <h2 className="section-title">WHAT WE DO</h2>
          <div className="service-list">
            {SERVICES.map((service) => (
              <article className="service" id={service.slug} key={service.slug}>
                <header>
                  <h3 className="service__name">{service.name}</h3>
                  {service.alsoCalled && (
                    <p className="service__aka">{service.alsoCalled}</p>
                  )}
                </header>
                <p className="prose">{service.summary}</p>
                <ul className="service__list">
                  {service.deliverables.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {service.evidence && (
                  <p className="service__evidence">{service.evidence}</p>
                )}
              </article>
            ))}
          </div>
        </PageShell>
      </section>

      <PageShell maxWidth="820px">
        <section id="enquire">
          <h2 className="section-title">START A CONVERSATION</h2>
          <p className="prose">
            Tell us what the game is and where it has got to. We will come back with what
            we think it needs, what we would charge, and what we would not take on.
          </p>
          <EnquiryForm />
          <p className="field__hint" style={{ marginTop: "20px" }}>
            Would rather not use a form? <a className="text-link" href={CONTACT_MAILTO}>Email us</a>{" "}
            or find us on{" "}
            <a
              className="text-link"
              href="https://discord.gg/Hs8eGcfp32"
              target="_blank"
              rel="noopener noreferrer"
            >
              Discord
            </a>.
          </p>
        </section>
      </PageShell>
    </SiteLayout>
  );
}

function HowToPlayPage() {
  return (
    <SiteLayout>
      <PageMeta
        title="How to Play"
        description="Turn structure, components and victory conditions for Cryptid Files, Fish Tales and Triad Paranormal."
      />
      <PageShell>
        <h1 style={pageHeadingStyle}>HOW TO PLAY</h1>
        <p style={pageDescriptionStyle}>
          Every game runs on the same spine: draw one card, do one thing, and answer what
          everyone else did.
        </p>

        <div className="jump">
          {GAMES.map((game) => (
            <a className="jump__link" href={`#/how-to-play#${game.slug}`} key={game.slug}>
              {game.name}
            </a>
          ))}
        </div>

        {GAMES.map((game) => (
          <section className="rules" id={game.slug} key={game.slug}>
            <h2 className="rules__title" style={{ color: game.accent }}>
              {game.name.toUpperCase()}
            </h2>
            <p className="prose">{game.blurb}</p>

            <h3 className="rules__sub">A turn</h3>
            <TurnList game={game} />

            <h3 className="rules__sub">Winning</h3>
            {game.victory.map((line) => (
              <p className="prose" key={line}>{line}</p>
            ))}
          </section>
        ))}

        <section className="rules">
          <h2 className="rules__title">{SHARED_RULES.title.toUpperCase()}</h2>
          <p className="prose">{SHARED_RULES.body}</p>
        </section>
      </PageShell>
    </SiteLayout>
  );
}

function MediaPage() {
  return (
    <SiteLayout>
      <PageMeta
        title="Media"
        description="Card art and key art from Cryptid Files and Fish Tales."
      />
      <PageShell maxWidth="1400px">
        <h1 style={pageHeadingStyle}>MEDIA</h1>
        <p style={pageDescriptionStyle}>
          Card faces and key art from every game. Press and creators are welcome to use
          any of it — a credit to Neon Crow Games is all we ask.
        </p>

        {GAMES.map((game) => (
          <section className="rules" key={game.slug}>
            <h2 className="rules__title" style={{ color: game.accent }}>
              {game.name.toUpperCase()}
            </h2>
            <img
              className="key-art"
              src={game.fan}
              alt={`A hand of ${game.name} cards`}
              width={1521}
              height={742}
              loading="lazy"
              decoding="async"
            />
            <CardStrip game={game} />
          </section>
        ))}
      </PageShell>
    </SiteLayout>
  );
}

function ContactPage() {
  return (
    <SiteLayout>
      <PageMeta
        title="Contact"
        description="Reach the Neon Crow Games team by email, Discord, X or Facebook."
      />
      <PageShell>
        <h1 style={pageHeadingStyle}>CONTACT</h1>
        <p style={pageDescriptionStyle}>
          Get in touch with the Neon Crow Games team.
        </p>

        <div style={{ marginTop: "clamp(28px, 5vw, 44px)" }}>
          <EnquiryForm />
        </div>

        <h2 className="section-title" style={{ marginTop: "clamp(40px, 7vw, 68px)" }}>
          OR FIND US HERE
        </h2>
        <div style={{
          marginTop: "40px",
          color: "#e0e0e0",
          fontSize: "18px",
          lineHeight: 1.8,
          overflowWrap: "break-word"
        }}>
          <p>
            <strong style={{ color: "#00ffff" }}>Email:</strong>{" "}
            <a href="mailto:neoncrowgames@gmail.com" className="text-link">
              neoncrowgames@gmail.com
            </a>
          </p>
          <p>
            <strong style={{ color: "#00ffff" }}>X (Twitter):</strong>{" "}
            <a
              href="https://x.com/neon_crow_games"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              @neon_crow_games
            </a>
          </p>
          <p>
            <strong style={{ color: "#00ffff" }}>Discord:</strong>{" "}
            <a
              href="https://discord.gg/Hs8eGcfp32"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              discord.gg/Hs8eGcfp32
            </a>
          </p>
          <p>
            <strong style={{ color: "#00ffff" }}>Facebook:</strong>{" "}
            <a
              href="https://www.facebook.com/profile.php?id=61562906850303"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              Neon Crow Games
            </a>
          </p>
        </div>
      </PageShell>
    </SiteLayout>
  );
}

function PrivacyPage() {
  const sectionStyle: React.CSSProperties = {
    marginTop: "40px",
    color: "#e0e0e0",
    fontSize: "16px",
    lineHeight: 1.8,
    overflowWrap: "break-word"
  };

  const sectionHeadingStyle: React.CSSProperties = {
    fontFamily: "'Archivo', 'Inter', sans-serif",
    fontSize: "clamp(22px, 4vw, 28px)",
    color: "#00ffff",
    marginTop: "40px",
    marginBottom: "20px",
    textShadow: "0 0 15px rgba(0, 255, 255, 0.5)",
    letterSpacing: "1px"
  };

  return (
    <SiteLayout>
      <PageMeta
        title="Privacy Policy"
        description="How Neon Crow Games collects, uses and safeguards your information."
      />
      <PageShell>
        <h1 style={pageHeadingStyle}>PRIVACY POLICY</h1>
        <p style={pageDescriptionStyle}>
          Last Updated: September 28, 2026
        </p>

        <div style={sectionStyle}>
          <p>
            At Neon Crow Games ("we," "us," or "our"), we are committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information
            when you visit our website and use our services, including Cryptid Files and Fish Tales.
          </p>

          <h2 style={sectionHeadingStyle}>1. Information We Collect</h2>
          <p><strong>Personal Information</strong></p>
          <p>
            We may collect personal information that you voluntarily provide to us when you:
          </p>
          <ul style={{ marginLeft: "20px" }}>
            <li>Register for an account</li>
            <li>Subscribe to our newsletter</li>
            <li>Contact us via email</li>
            <li>Participate in our community features</li>
          </ul>
          <p>This information may include:</p>
          <ul style={{ marginLeft: "20px" }}>
            <li>Name and username</li>
            <li>Email address</li>
            <li>Password (securely encrypted)</li>
            <li>Game preferences and settings</li>
          </ul>

          <p style={{ marginTop: "20px" }}><strong>Automatically Collected Information</strong></p>
          <p>
            When you visit our website, we may automatically collect certain information about your device,
            including information about your web browser, IP address, time zone, and some of the cookies
            installed on your device.
          </p>

          <h2 style={sectionHeadingStyle}>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul style={{ marginLeft: "20px" }}>
            <li>Provide, operate, and maintain our services</li>
            <li>Create and manage your account</li>
            <li>Send you newsletters and marketing communications (if you've subscribed)</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Improve and optimize our website and games</li>
            <li>Detect and prevent fraud and abuse</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 style={sectionHeadingStyle}>3. Data Storage and Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal
            information against unauthorized access, alteration, disclosure, or destruction. Your passwords
            are encrypted using industry-standard bcrypt hashing, and we use secure protocols for data
            transmission.
          </p>
          <p>
            However, please note that no method of transmission over the internet or electronic storage is
            100% secure, and we cannot guarantee absolute security.
          </p>

          <h2 style={sectionHeadingStyle}>4. Cookies and Tracking Technologies</h2>
          <p>
            We may use cookies and similar tracking technologies to track activity on our website and hold
            certain information. Cookies are files with a small amount of data that may include an anonymous
            unique identifier. You can instruct your browser to refuse all cookies or to indicate when a
            cookie is being sent.
          </p>

          <h2 style={sectionHeadingStyle}>5. Third-Party Services</h2>
          <p>
            We may employ third-party companies and individuals to facilitate our service, provide the
            service on our behalf, perform service-related services, or assist us in analyzing how our
            service is used. These third parties have access to your personal information only to perform
            these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
          </p>
          <p>
            This website uses <strong>Google Analytics</strong>, a web analytics service provided by
            Google LLC, to understand which pages people visit and how they arrive here. Google Analytics
            sets cookies and receives your IP address, which Google uses to approximate your location and
            then discards rather than storing. We use it in aggregate only; it is never used to identify
            you personally. You can opt out across every site that uses it with Google's{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              browser add-on
            </a>, or by blocking cookies as described above.
          </p>

          <h2 style={sectionHeadingStyle}>6. Email Communications</h2>
          <p>
            If you subscribe to our newsletter, we will use your email address to send you updates about
            Neon Crow Games and our products. You may unsubscribe from these communications at any time by
            clicking the "unsubscribe" link in any email we send, or by contacting us directly at
            neoncrowgames@gmail.com.
          </p>

          <h2 style={sectionHeadingStyle}>7. Children's Privacy</h2>
          <p>
            Our services are not intended for children under the age of 13. We do not knowingly collect
            personal information from children under 13. If you are a parent or guardian and you are aware
            that your child has provided us with personal information, please contact us so that we can take
            necessary action.
          </p>

          <h2 style={sectionHeadingStyle}>8. Your Data Rights</h2>
          <p>Depending on your location, you may have the following rights regarding your personal data:</p>
          <ul style={{ marginLeft: "20px" }}>
            <li><strong>Access:</strong> Request access to your personal data</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a structured format</li>
            <li><strong>Objection:</strong> Object to our processing of your personal data</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent where we rely on consent to process your data</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at neoncrowgames@gmail.com.
          </p>

          <h2 style={sectionHeadingStyle}>9. Data Retention</h2>
          <p>
            We will retain your personal information only for as long as necessary to fulfill the purposes
            outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            When we no longer need your information, we will securely delete or anonymize it.
          </p>

          <h2 style={sectionHeadingStyle}>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and maintained on computers located outside of your state,
            province, country, or other governmental jurisdiction where data protection laws may differ. By
            using our services, you consent to the transfer of your information to these locations.
          </p>

          <h2 style={sectionHeadingStyle}>11. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting
            the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to
            review this Privacy Policy periodically for any changes.
          </p>

          <h2 style={sectionHeadingStyle}>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p style={{ marginTop: "10px" }}>
            <strong>Neon Crow Games</strong><br />
            Email: <a href="mailto:neoncrowgames@gmail.com" className="text-link">
              neoncrowgames@gmail.com
            </a><br />
            X (Twitter): <a
              href="https://x.com/neon_crow_games"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              @neon_crow_games
            </a><br />
            Discord: <a
              href="https://discord.gg/Hs8eGcfp32"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              discord.gg/Hs8eGcfp32
            </a><br />
            Facebook: <a
              href="https://www.facebook.com/profile.php?id=61562906850303"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              Neon Crow Games
            </a>
          </p>

          <div style={{
            marginTop: "60px",
            padding: "clamp(20px, 4vw, 30px)",
            border: "1px solid rgba(0, 255, 255, 0.2)",
            borderRadius: "8px",
            background: "rgba(0, 255, 255, 0.03)"
          }}>
            <p style={{ fontSize: "14px", color: "#9ca3af", margin: 0 }}>
              This privacy policy is designed to comply with general data protection principles.
              Depending on your jurisdiction, additional rights and protections may apply. We are
              committed to transparency and protecting your privacy in accordance with applicable laws.
            </p>
          </div>
        </div>
      </PageShell>
    </SiteLayout>
  );
}

// ---------------------------------------------
// Styles
// ---------------------------------------------
const pageHeadingStyle: React.CSSProperties = {
  fontFamily: "'Archivo', 'Inter', sans-serif",
  fontSize: "clamp(30px, 6.5vw, 60px)",
  fontWeight: 700,
  color: "#00ffff",
  textShadow: "0 0 20px rgba(0, 255, 255, 0.6)",
  textTransform: "uppercase",
  letterSpacing: "2px",
  marginBottom: "20px",
  overflowWrap: "break-word"
};

const pageDescriptionStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: "clamp(17px, 2.4vw, 24px)",
  color: "#e0e0e0",
  lineHeight: 1.6
};

export default App;
